import * as React from "react";
import { Theme, makeStyles, Tooltip } from "@material-ui/core";
import { LevelThreshold } from "../../core/models/LevelThreshold";
import { calculateLevelPercentage, getLevelColor } from "../../helpers/installationTankHelpers";
import { getThresholdDescriptionFromKey } from "../../helpers/thresholdLevelsHelpers";
import { installationLevelStatus } from "../../core/models/LevelStatus";

type styleProps = {
  tankHeight?: number;
  borderRadius?: number;
  fullHeight?: boolean;
};

const useStyles = makeStyles<Theme, styleProps>((theme: Theme) => ({
  indicatorWrapper: (props) => ({
    "borderRadius": props.borderRadius,
    "height": props.tankHeight,
    "flex": 1,
    "position": "relative",

    "& div:not(.threshold)": {
      borderRadius: props.fullHeight
        ? props.borderRadius
        : `${props.borderRadius}px 0 0 ${props.borderRadius}px`,
      height: props.tankHeight,
      position: "absolute",
      top: 0,
    },

    "& .container": {
      borderRadius: props.borderRadius,
      width: "100%",
      background: theme.customPaper.background.tankContainer,
    },

    "& .threshold": {
      width: 1,
      border: `1px dotted ${theme.customPalette.border.gray}`,
      position: "absolute",
      height: "100%",
    },
  }),
}));

type TankIndicatorProps = {
  level?: number;
  capacity?: number;
  thresholds: LevelThreshold;
  tankHeight?: number;
  borderRadius?: number;
  showThresholds?: boolean;
  status?: installationLevelStatus;
};

function TankIndicator(props: TankIndicatorProps) {
  const {
    level,
    thresholds,
    capacity,
    tankHeight = 20,
    borderRadius = 5,
    showThresholds = true,
    status,
  } = props;

  const currentPercentageLevel = calculateLevelPercentage(level, capacity);

  const classes = useStyles({
    tankHeight,
    borderRadius,
    fullHeight: currentPercentageLevel === 100,
  });

  const styleCardIndicator = (): React.CSSProperties => {
    return {
      width: Math.min(currentPercentageLevel, 100).toString() + "%",
      backgroundColor: getLevelColor(status || level, thresholds),
    };
  };

  const renderThresholds = () => {
    const thresholdsComponents = [];

    for (const threshold in thresholds) {
      const thresholdValue = thresholds[threshold as keyof LevelThreshold];

      if (!thresholdValue) {
        continue;
      }

      const percentageLevel = calculateLevelPercentage(thresholdValue, capacity);

      if (0 >= percentageLevel || percentageLevel >= 100) {
        continue;
      }

      const tooltipText = getThresholdDescriptionFromKey(threshold as keyof LevelThreshold);

      const component = (
        <Tooltip key={`threshold_${threshold}`} title={`${tooltipText}: ${percentageLevel}%`}>
          <div className="threshold" style={{ left: `calc(${percentageLevel}% - 1px)` }} />
        </Tooltip>
      );

      thresholdsComponents.push(component);
    }

    return <>{thresholdsComponents.map((x) => x)}</>;
  };

  return (
    <div className={classes.indicatorWrapper}>
      <div className="container" />
      <div style={styleCardIndicator()} />
      {showThresholds && renderThresholds()}
    </div>
  );
}

export default TankIndicator;
